/**
 * Fuzzy Search Utility Library
 * 
 * Provides intelligent search capabilities for bank references with:
 * - Levenshtein distance calculation for edit distance
 * - Keyboard adjacency detection for common typos
 * - Fuzzy matching with customizable tolerance
 * - Wildcard pattern matching
 * 
 * @version 1.0.0
 * @created 2025-12-23
 */

// QWERTY keyboard layout for adjacency detection
const KEYBOARD_ADJACENCY: Record<string, string[]> = {
 // Number row
 '1': ['2', 'q'],
 '2': ['1', '3', 'q', 'w'],
 '3': ['2', '4', 'w', 'e'],
 '4': ['3', '5', 'e', 'r'],
 '5': ['4', '6', 'r', 't'],
 '6': ['5', '7', 't', 'y'],
 '7': ['6', '8', 'y', 'u'],
 '8': ['7', '9', 'u', 'i'],
 '9': ['8', '0', 'i', 'o'],
 '0': ['9', 'o', 'p'],
 // Top row
 'q': ['1', '2', 'w', 'a'],
 'w': ['2', '3', 'q', 'e', 'a', 's'],
 'e': ['3', '4', 'w', 'r', 's', 'd'],
 'r': ['4', '5', 'e', 't', 'd', 'f'],
 't': ['5', '6', 'r', 'y', 'f', 'g'],
 'y': ['6', '7', 't', 'u', 'g', 'h'],
 'u': ['7', '8', 'y', 'i', 'h', 'j'],
 'i': ['8', '9', 'u', 'o', 'j', 'k'],
 'o': ['9', '0', 'i', 'p', 'k', 'l'],
 'p': ['0', 'o', 'l'],
 // Middle row
 'a': ['q', 'w', 's', 'z'],
 's': ['w', 'e', 'a', 'd', 'z', 'x'],
 'd': ['e', 'r', 's', 'f', 'x', 'c'],
 'f': ['r', 't', 'd', 'g', 'c', 'v'],
 'g': ['t', 'y', 'f', 'h', 'v', 'b'],
 'h': ['y', 'u', 'g', 'j', 'b', 'n'],
 'j': ['u', 'i', 'h', 'k', 'n', 'm'],
 'k': ['i', 'o', 'j', 'l', 'm'],
 'l': ['o', 'p', 'k'],
 // Bottom row
 'z': ['a', 's', 'x'],
 'x': ['s', 'd', 'z', 'c'],
 'c': ['d', 'f', 'x', 'v'],
 'v': ['f', 'g', 'c', 'b'],
 'b': ['g', 'h', 'v', 'n'],
 'n': ['h', 'j', 'b', 'm'],
 'm': ['j', 'k', 'n'],
};

/**
 * Calculate Levenshtein distance between two strings
 * Returns the minimum number of edits needed to transform str1 into str2
 */
export function levenshteinDistance(str1: string, str2: string): number {
 const m = str1.length;
 const n = str2.length;
 
 // Create 2D array for dynamic programming
 const dp: number[][] = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
 
 // Initialize base cases
 for (let i = 0; i <= m; i++) dp[i][0] = i;
 for (let j = 0; j <= n; j++) dp[0][j] = j;
 
 // Fill the DP table
 for (let i = 1; i <= m; i++) {
 for (let j = 1; j <= n; j++) {
 if (str1[i - 1].toLowerCase() === str2[j - 1].toLowerCase()) {
 dp[i][j] = dp[i - 1][j - 1];
 } else {
 dp[i][j] = 1 + Math.min(
 dp[i - 1][j], // deletion
 dp[i][j - 1], // insertion
 dp[i - 1][j - 1] // substitution
 );
 }
 }
 }
 
 return dp[m][n];
}

/**
 * Check if two characters are adjacent on a QWERTY keyboard
 */
export function areKeysAdjacent(char1: string, char2: string): boolean {
 const c1 = char1.toLowerCase();
 const c2 = char2.toLowerCase();
 return KEYBOARD_ADJACENCY[c1]?.includes(c2) || false;
}

/**
 * Calculate weighted edit distance considering keyboard adjacency
 * Keyboard-adjacent typos are weighted less heavily
 */
export function weightedEditDistance(str1: string, str2: string): number {
 const m = str1.length;
 const n = str2.length;
 
 const dp: number[][] = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
 
 for (let i = 0; i <= m; i++) dp[i][0] = i;
 for (let j = 0; j <= n; j++) dp[0][j] = j;
 
 for (let i = 1; i <= m; i++) {
 for (let j = 1; j <= n; j++) {
 if (str1[i - 1].toLowerCase() === str2[j - 1].toLowerCase()) {
 dp[i][j] = dp[i - 1][j - 1];
 } else {
 // Check if it's a keyboard-adjacent substitution (weight = 0.5)
 const substitutionCost = areKeysAdjacent(str1[i - 1], str2[j - 1]) ? 0.5 : 1;
 dp[i][j] = Math.min(
 dp[i - 1][j] + 1, // deletion
 dp[i][j - 1] + 1, // insertion
 dp[i - 1][j - 1] + substitutionCost // substitution
 );
 }
 }
 }
 
 return dp[m][n];
}

/**
 * Check if two characters in adjacent positions are transposed
 */
export function hasTransposition(str1: string, str2: string): boolean {
 if (str1.length !== str2.length) return false;
 
 let diffCount = 0;
 let diffPositions: number[] = [];
 
 for (let i = 0; i < str1.length; i++) {
 if (str1[i].toLowerCase() !== str2[i].toLowerCase()) {
 diffCount++;
 diffPositions.push(i);
 if (diffCount > 2) return false;
 }
 }
 
 if (diffCount === 2 && diffPositions[1] - diffPositions[0] === 1) {
 const p1 = diffPositions[0];
 const p2 = diffPositions[1];
 return str1[p1].toLowerCase() === str2[p2].toLowerCase() && 
 str1[p2].toLowerCase() === str2[p1].toLowerCase();
 }
 
 return false;
}

/**
 * Calculate similarity score between 0 and 1
 * 1 = identical, 0 = completely different
 */
export function similarityScore(str1: string, str2: string): number {
 if (str1 === str2) return 1;
 if (!str1 || !str2) return 0;

 const maxLen = Math.max(str1.length, str2.length);
 const distance = weightedEditDistance(str1, str2);

 return Math.max(0, 1 - distance / maxLen);
}

/**
 * Character comparison result for visual highlighting
 */
export interface CharacterComparison {
 searchChar: string;
 matchChar: string;
 status: 'match' | 'mismatch' | 'adjacent' | 'missing' | 'extra';
 position: number;
}

/**
 * Compare two strings character by character and return comparison results
 * Uses alignment to handle insertions/deletions
 */
export function compareCharacters(searchTerm: string, candidateTerm: string): {
 comparisons: CharacterComparison[];
 matchCount: number;
 mismatchCount: number;
 adjacentCount: number;
 alignedSearch: string;
 alignedCandidate: string;
} {
 const s1 = searchTerm.toUpperCase();
 const s2 = candidateTerm.toUpperCase();

 // Use dynamic programming to find optimal alignment
 const m = s1.length;
 const n = s2.length;

 // Build DP table for edit distance with traceback
 const dp: number[][] = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));

 for (let i = 0; i <= m; i++) dp[i][0] = i;
 for (let j = 0; j <= n; j++) dp[0][j] = j;

 for (let i = 1; i <= m; i++) {
 for (let j = 1; j <= n; j++) {
 if (s1[i - 1] === s2[j - 1]) {
 dp[i][j] = dp[i - 1][j - 1];
 } else {
 dp[i][j] = 1 + Math.min(
 dp[i - 1][j], // deletion
 dp[i][j - 1], // insertion
 dp[i - 1][j - 1] // substitution
 );
 }
 }
 }

 // Traceback to find alignment
 const comparisons: CharacterComparison[] = [];
 let alignedSearch = '';
 let alignedCandidate = '';
 let i = m, j = n;
 let position = Math.max(m, n) - 1;

 while (i > 0 || j > 0) {
 if (i > 0 && j > 0 && s1[i - 1] === s2[j - 1]) {
 // Match
 comparisons.unshift({
 searchChar: searchTerm[i - 1],
 matchChar: candidateTerm[j - 1],
 status: 'match',
 position
 });
 alignedSearch = searchTerm[i - 1] + alignedSearch;
 alignedCandidate = candidateTerm[j - 1] + alignedCandidate;
 i--;
 j--;
 } else if (i > 0 && j > 0 && dp[i][j] === dp[i - 1][j - 1] + 1) {
 // Substitution - check if keyboard adjacent
 const isAdjacent = areKeysAdjacent(s1[i - 1], s2[j - 1]);
 comparisons.unshift({
 searchChar: searchTerm[i - 1],
 matchChar: candidateTerm[j - 1],
 status: isAdjacent ? 'adjacent' : 'mismatch',
 position
 });
 alignedSearch = searchTerm[i - 1] + alignedSearch;
 alignedCandidate = candidateTerm[j - 1] + alignedCandidate;
 i--;
 j--;
 } else if (j > 0 && (i === 0 || dp[i][j] === dp[i][j - 1] + 1)) {
 // Insertion (extra char in candidate)
 comparisons.unshift({
 searchChar: '-',
 matchChar: candidateTerm[j - 1],
 status: 'extra',
 position
 });
 alignedSearch = '-' + alignedSearch;
 alignedCandidate = candidateTerm[j - 1] + alignedCandidate;
 j--;
 } else {
 // Deletion (missing char in candidate)
 comparisons.unshift({
 searchChar: searchTerm[i - 1],
 matchChar: '-',
 status: 'missing',
 position
 });
 alignedSearch = searchTerm[i - 1] + alignedSearch;
 alignedCandidate = '-' + alignedCandidate;
 i--;
 }
 position--;
 }

 // Count each type
 let matchCount = 0;
 let mismatchCount = 0;
 let adjacentCount = 0;

 for (const comp of comparisons) {
 if (comp.status === 'match') matchCount++;
 else if (comp.status === 'mismatch' || comp.status === 'missing' || comp.status === 'extra') mismatchCount++;
 else if (comp.status === 'adjacent') adjacentCount++;
 }

 return {
 comparisons,
 matchCount,
 mismatchCount,
 adjacentCount,
 alignedSearch,
 alignedCandidate
 };
}

/**
 * Get CSS class for character comparison status
 */
export function getCharStatusColor(status: CharacterComparison['status']): string {
 switch (status) {
 case 'match': return 'text-green-400 bg-green-900/30';
 case 'mismatch': return 'text-red-400 bg-red-900/30';
 case 'adjacent': return 'text-amber-400 bg-amber-900/30';
 case 'missing': return 'text-red-400 bg-red-900/30 line-through';
 case 'extra': return 'text-blue-400 bg-blue-900/30';
 default: return 'text-white';
 }
}

/**
 * Check if a string matches a wildcard pattern
 * Supports * for multiple characters and ? for single character
 */
export function matchesWildcard(text: string, pattern: string): boolean {
 const regexPattern = pattern
 .toLowerCase()
 .replace(/[.+^${}()|[\]\\]/g, '\\$&') // Escape regex special chars
 .replace(/\*/g, '.*') // * matches any characters
 .replace(/\?/g, '.'); // ? matches single character

 return new RegExp(`^${regexPattern}$`, 'i').test(text);
}

/**
 * Check if a string contains partial match
 */
export function containsPartialMatch(text: string, search: string): boolean {
 return text.toLowerCase().includes(search.toLowerCase());
}

export interface FuzzyMatchResult {
 value: string;
 score: number;
 matchType: 'exact' | 'partial' | 'fuzzy' | 'transposition' | 'keyboard_typo';
 editDistance: number;
}

/**
 * Find fuzzy matches from a list of candidates
 * Returns sorted array of matches with scores
 */
export function findFuzzyMatches(
 searchTerm: string,
 candidates: string[],
 options: {
 minScore?: number; // Minimum similarity score (0-1), default 0.5
 maxResults?: number; // Maximum number of results, default 10
 maxEditDistance?: number; // Maximum edit distance to consider, default 3
 } = {}
): FuzzyMatchResult[] {
 const { minScore = 0.5, maxResults = 10, maxEditDistance = 3 } = options;

 if (!searchTerm || searchTerm.length < 3) return [];

 const results: FuzzyMatchResult[] = [];
 const searchLower = searchTerm.toLowerCase();

 for (const candidate of candidates) {
 if (!candidate) continue;

 const candidateLower = candidate.toLowerCase();

 // Check for exact match (case-insensitive)
 if (candidateLower === searchLower) {
 results.push({
 value: candidate,
 score: 1,
 matchType: 'exact',
 editDistance: 0
 });
 continue;
 }

 // Check for partial match (contains)
 if (containsPartialMatch(candidate, searchTerm)) {
 const score = searchTerm.length / candidate.length;
 results.push({
 value: candidate,
 score: Math.min(0.95, 0.7 + score * 0.2), // Partial matches score 0.7-0.9
 matchType: 'partial',
 editDistance: Math.abs(candidate.length - searchTerm.length)
 });
 continue;
 }

 // Check for transposition
 if (hasTransposition(searchTerm, candidate)) {
 results.push({
 value: candidate,
 score: 0.9, // Transpositions are very likely to be the intended match
 matchType: 'transposition',
 editDistance: 1
 });
 continue;
 }

 // Calculate edit distance and similarity
 const editDistance = levenshteinDistance(searchTerm, candidate);

 if (editDistance <= maxEditDistance) {
 const score = similarityScore(searchTerm, candidate);

 if (score >= minScore) {
 // Determine if it's likely a keyboard typo
 let isKeyboardTypo = false;
 if (searchTerm.length === candidate.length && editDistance === 1) {
 for (let i = 0; i < searchTerm.length; i++) {
 if (searchTerm[i] !== candidate[i] && areKeysAdjacent(searchTerm[i], candidate[i])) {
 isKeyboardTypo = true;
 break;
 }
 }
 }

 results.push({
 value: candidate,
 score: isKeyboardTypo ? Math.min(score * 1.1, 0.95) : score,
 matchType: isKeyboardTypo ? 'keyboard_typo' : 'fuzzy',
 editDistance
 });
 }
 }
 }

 // Sort by score descending, then by edit distance ascending
 results.sort((a, b) => {
 if (b.score !== a.score) return b.score - a.score;
 return a.editDistance - b.editDistance;
 });

 return results.slice(0, maxResults);
}

/**
 * Format match type for display
 */
export function getMatchTypeLabel(matchType: FuzzyMatchResult['matchType']): string {
 switch (matchType) {
 case 'exact': return 'Exact match';
 case 'partial': return 'Contains match';
 case 'transposition': return 'Character swap';
 case 'keyboard_typo': return 'Possible typo';
 case 'fuzzy': return 'Similar';
 default: return 'Match';
 }
}

/**
 * Get match type color for UI
 */
export function getMatchTypeColor(matchType: FuzzyMatchResult['matchType']): string {
 switch (matchType) {
 case 'exact': return 'text-green-400';
 case 'partial': return 'text-blue-400';
 case 'transposition': return 'text-amber-400';
 case 'keyboard_typo': return 'text-orange-400';
 case 'fuzzy': return 'text-purple-400';
 default: return 'text-white';
 }
}

